import { useNavigate } from "react-router-dom";
import type { ComponentProps, KeyboardEvent, MouseEvent } from "react";
import { clickTargetSkipsRowNavigation } from "@/lib/rowNavigation";
import { cn } from "@/lib/utils";

function useRecordNavigation(href: string) {
  const navigate = useNavigate();

  function openFromClick(event: MouseEvent<HTMLTableRowElement>) {
    if (event.defaultPrevented) return;
    if (clickTargetSkipsRowNavigation(event.target)) return;
    void navigate(href);
  }

  function openFromKey(event: KeyboardEvent<HTMLTableRowElement>) {
    if (event.key !== "Enter" && event.key !== " ") return;
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    void navigate(href);
  }

  return { openFromClick, openFromKey };
}

type NativeRowProps = ComponentProps<"tr"> & { href: string };

export function RecordLinkRow({
  href,
  className,
  onClick,
  onKeyDown,
  tabIndex,
  ...props
}: NativeRowProps) {
  const { openFromClick, openFromKey } = useRecordNavigation(href);
  return (
    <tr
      {...props}
      tabIndex={tabIndex ?? 0}
      className={cn(
        "cursor-pointer border-b border-gray-100 hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none dark:border-white/5 dark:hover:bg-white/5 dark:focus-visible:bg-white/5",
        className,
      )}
      onClick={(event) => {
        onClick?.(event);
        openFromClick(event);
      }}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        openFromKey(event);
      }}
    />
  );
}
